import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { createRef, FC, useCallback, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useArchiveStatusOtherDocumentMutation,
  useDeleteOtherDocumentMutation,
  useGetOtherClientDocument,
  useUnmapOtherDocumentMutation,
  useUpdateStatusOtherDocumentMutation,
} from "../../../../../api/services/client/clientApis";
import {
  FormController,
  MuiBox,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  WarningConfirmationModal as OtherDocumentWarningConfirmationModal,
  RowProps,
} from "../../../../../components";
import { MuiLink } from "../../../../../components/link/MuiLink";
import { StatusBadge } from "../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import {
  arrayOfObjectsIncludesValue,
  getParsedErrorMessage,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import {
  ClientDocument,
  CreateClientDocumentsFormKey,
  DocumentsProps,
} from "../../../modelsAndSchemas/Models";
import { documentSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

import { ArchiveStatusOtherDocumentParams } from "../../../../../api/services/client/models";
import MuiChipWithPopover from "../../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  DOCUMENT_SITE_CORE_KEY,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
} from "../../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { createClientSelector } from "../../../../../redux/slices/client/createClientSelector";
import ArchiveContractDocumentModal from "./archiveContractDocumentModal/ArchiveContractDocumentModal";
import { OptionMenu } from "./optionMenu/OptionMenu";
import { OtherDocumentListWrapper } from "./OtherDocumentList.style";

export const pageId = "create-client-other-document-list";
const ACTION_ACTIVE = "active";
const ACTION_NO_LONGER_VALID = "noLongerValid";
const ACTION_ARCHIVE = "archive";

const PARAMS_ACTIVE = "Active";
const PARAMS_NO_LONGER_VALID = "NoLongerValid";
export const OtherDocumentList: FC<DocumentsProps> = ({
  rows,
  onDocumentView,
  successHandler,
  onDocumentEdit,
  isEditable = true,
}) => {
  const selectedDocumentIndexRef = useRef<string>();
  const selectedArchiveDocumentIndexRef = useRef<ClientDocument>();
  const selectedDocumentActionRef = useRef<string>(ACTION_ACTIVE);
  const {
    mutateAsync: deleteOtherDocument,
    isPending: isDeleteOtherDocumentPending,
    isSuccess: isDeleteOtherDocumentSuccess,
    isError: isDeleteOtherDocumentError,
    error: deleteOtherDocumentError,
  } = useDeleteOtherDocumentMutation();
  const {
    mutateAsync: updateStatusOtherDocument,
    isPending: isUpdateStatusOtherDocumentPending,
    isSuccess: isUpdateStatusOtherDocumentSuccess,
    isError: isUpdateStatusOtherDocumentError,
    error: updateStatusOtherDocumentError,
  } = useUpdateStatusOtherDocumentMutation();

  const {
    mutateAsync: getOtherClientDocument,
    data: otherClientDocumentData,
    isSuccess: isGetOtherClientDocumentSuccess,
  } = useGetOtherClientDocument();

  const {
    mutateAsync: unmapOtherDocumentsMutation,
    isPending: isUnmapOtherDocumentsPending,
    isSuccess: isUnmapOtherDocumentsSuccess,
    isError: isUnmapOtherDocumentsError,
  } = useUnmapOtherDocumentMutation();

  const {
    mutateAsync: archiveStatusContractDocument,
    isPending: isArchiveStatusContractDocumentPending,
    isSuccess: isArchiveStatusContractDocumentSuccess,
    isError: isArchiveStatusContractDocumentError,
    error: archiveStatusContractDocumentError,
  } = useArchiveStatusOtherDocumentMutation();

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { id: clientId } = useSelector(createClientSelector);
  const [showDeleteOtherDocumentModal, setShowDeleteOtherDocumentModal] =
    useState(false);
  const [showStatusUpdateOtherModal, setShowStatusUpdateOtherModal] =
    useState(false);
  const [showArchiveContractModal, setShowArchiveContractModal] =
    useState(false);
  const gridRef = createRef<HTMLDivElement>();

  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [filteredResults, setFilteredResults] = useState<ClientDocument[]>([
    ...rows,
  ]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const { control, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [CreateClientDocumentsFormKey.documentName]: "",
      [CreateClientDocumentsFormKey.documentId]: "",
      [CreateClientDocumentsFormKey.notes]: "",
      [CreateClientDocumentsFormKey.status]: "",
      [CreateClientDocumentsFormKey.usedByClient]: "",
      [CreateClientDocumentsFormKey.documentOrigin]: "",
    },
  });

  const tableColumnFilterWatch = useWatch({ control });

  useEffect(() => {
    const {
      documentName,
      status,
      notes,
      documentId,
      usedByClient,
      documentOrigin,
    } = getValues();

    const filteredDocuments = [...rows].filter((item) => {
      const matchesDocumentId = documentId
        ? item.documentId.toLowerCase().includes(documentId.toLowerCase())
        : true;

      const matchesDocumentName = documentName
        ? item.documentName.toLowerCase().includes(documentName.toLowerCase())
        : true;

      const matchesUsedByClient = arrayOfObjectsIncludesValue(
        usedByClient,
        (item?.usedByClient ?? []).map((client) => ({ label: client })),
        "label",
      );

      const matchesNotes = notes
        ? item.notes.toLowerCase().includes(notes.toLowerCase())
        : true;
      const matchesStatus =
        isEmpty(status) ||
        fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.othersStatusList,
          item.status,
        ).toLowerCase() === status.toLowerCase();

      const matchesDocumentOrigin =
        isEmpty(documentOrigin) ||
        fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.documentOriginList,
          getValueOrBlank(item.documentOrigin),
        ).toLowerCase() === documentOrigin.toLowerCase();

      return (
        matchesDocumentName &&
        matchesDocumentId &&
        matchesStatus &&
        matchesNotes &&
        matchesUsedByClient &&
        matchesDocumentOrigin
      );
    });

    setFilteredResults([...filteredDocuments]);
    resetPagination();
  }, [tableColumnFilterWatch]);

  useEffect(() => {
    reset();
    resetPagination();

    setFilteredResults([...rows]);
  }, [rows]);

  useEffect(() => {
    if (isUnmapOtherDocumentsSuccess) {
      displayToast(
        getLabel(documentSiteCoreKeys.documentUnmappedToastMessage),
        "success",
      );
      successHandler();
    }
  }, [isUnmapOtherDocumentsSuccess]);

  useEffect(() => {
    if (isUnmapOtherDocumentsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isUnmapOtherDocumentsError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        unmapOtherDocumentsLoader: {
          isVisible: isUnmapOtherDocumentsPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          unmapOtherDocumentsLoader: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isUnmapOtherDocumentsPending]);

  useEffect(() => {
    if (isUpdateStatusOtherDocumentSuccess) {
      const getSuccessMessage = () => {
        if (selectedDocumentActionRef.current === ACTION_NO_LONGER_VALID) {
          return getLabel(documentSiteCoreKeys.documentMarkedNoLongerValid);
        } else if (selectedDocumentActionRef.current === ACTION_ACTIVE) {
          return getLabel(documentSiteCoreKeys.documentMarkActiveMessage);
        }
      };
      dispatch(
        toggleToast({
          open: true,
          message: getSuccessMessage() ?? "",
        }),
      );
      successHandler();
    }
  }, [isUpdateStatusOtherDocumentSuccess]);

  const isLoading =
    isArchiveStatusContractDocumentPending ||
    isUpdateStatusOtherDocumentPending ||
    isDeleteOtherDocumentPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
  }, [isLoading]);

  const error =
    updateStatusOtherDocumentError || archiveStatusContractDocumentError;
  const isError =
    isUpdateStatusOtherDocumentError || isArchiveStatusContractDocumentError;

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: getParsedErrorMessage(error),
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    if (
      isDeleteOtherDocumentSuccess ||
      isArchiveStatusContractDocumentSuccess
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(
            isDeleteOtherDocumentSuccess
              ? documentSiteCoreKeys.documentDeleted
              : documentSiteCoreKeys.documentArchived,
          ),
        }),
      );
      successHandler();
    }
  }, [isDeleteOtherDocumentSuccess, isArchiveStatusContractDocumentSuccess]);

  useEffect(() => {
    if (isDeleteOtherDocumentError && deleteOtherDocumentError) {
      dispatch(
        toggleToast({
          open: true,
          message: getParsedErrorMessage(isDeleteOtherDocumentError),
        }),
      );
    }
  }, [isDeleteOtherDocumentError, deleteOtherDocumentError]);

  const getLabel = (key: string) => {
    return fetchLabel(documentSiteCoreKeys.documentsTitle, key);
  };

  const handleDeleteOtherDocumentModalClose = () => {
    if (showStatusUpdateOtherModal) {
      setShowStatusUpdateOtherModal(false);
    }

    if (showDeleteOtherDocumentModal) {
      setShowDeleteOtherDocumentModal(false);
    }
  };

  const handleOtherDocumentDeleteClick = () => {
    const documentId = selectedDocumentIndexRef.current ?? "";
    if (isNotEmpty(documentId)) {
      setShowDeleteOtherDocumentModal(false);
      deleteOtherDocument({ documentId });
    }
  };

  const deleteOtherDocumentAction = (row: ClientDocument) => {
    if (!showDeleteOtherDocumentModal) {
      selectedDocumentIndexRef.current = row.documentId ?? "";
      setShowDeleteOtherDocumentModal(true);
    }
  };

  const archiveOtherDocumentAction = (row: ClientDocument) => {
    if (!showStatusUpdateOtherModal) {
      selectedArchiveDocumentIndexRef.current = row;
      selectedDocumentActionRef.current = ACTION_ARCHIVE;
      setShowArchiveContractModal(true);
    }
  };

  const handleOtherDocumentAction = () => {
    const documentId = selectedDocumentIndexRef.current ?? "";
    if (isNotEmpty(documentId)) {
      setShowStatusUpdateOtherModal(false);
      updateStatusOtherDocument({
        documentId,
        status:
          selectedDocumentActionRef.current === ACTION_ACTIVE
            ? PARAMS_ACTIVE
            : PARAMS_NO_LONGER_VALID,
      });
    }
  };
  const noLongerValidOtherDocumentAction = (row: ClientDocument) => {
    if (!showStatusUpdateOtherModal) {
      selectedDocumentIndexRef.current = row.documentId ?? "";
      selectedDocumentActionRef.current = ACTION_NO_LONGER_VALID;
      setShowStatusUpdateOtherModal(true);
    }
  };
  const markAsActiveOtherDocumentAction = (row: ClientDocument) => {
    if (!showStatusUpdateOtherModal) {
      selectedDocumentIndexRef.current = row.documentId ?? "";
      selectedDocumentActionRef.current = ACTION_ACTIVE;
      setShowStatusUpdateOtherModal(true);
    }
  };

  const unMapInheritedOtherDocumentAction = (row: ClientDocument) => {
    unmapOtherDocumentsMutation({
      clientId: clientId,
      otherDocumentId: row.documentId,
    });
  };

  useEffect(() => {
    if (isGetOtherClientDocumentSuccess) {
      onDocumentView &&
        onDocumentView({
          ...otherClientDocumentData,
          status: otherClientDocumentData.status,
          documentType: DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
        });
    }
  }, [isGetOtherClientDocumentSuccess]);

  const fetchDocumentData = async (documentId: string) => {
    getOtherClientDocument(documentId);
  };

  const columns = [
    {
      id: CreateClientDocumentsFormKey.documentName,
      label: getLabel(documentSiteCoreKeys.documentName),
      isSortVisible: true,
      className: "cell-documentName",
      render: (row: any) =>
        !row.documentName ? null : (
          <MuiLink
            onClick={() => {
              fetchDocumentData(row.documentId);
            }}
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link document-name-link"
                text={row.documentName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: CreateClientDocumentsFormKey.usedByClient,
      label: getLabel(documentSiteCoreKeys.usedByClient),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => {
        if (isEmpty(row?.usedByClient)) {
          return <MuiTypography variant="h3">{EMPTY_VALUE_DASH}</MuiTypography>;
        }
        const chipOptions = row.usedByClient.map((item: string) => ({
          label: item,
          value: item,
        }));
        return (
          <MuiChipWithPopover
            data={chipOptions}
            isEditable={false}
            headerLabel={getLabel(documentSiteCoreKeys.usedByClient)}
            gridRef={gridRef}
            countToWrap={1}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        );
      },
    },
    {
      id: CreateClientDocumentsFormKey.status,
      label: getLabel(documentSiteCoreKeys.status),
      isSortVisible: true,
      className: "cell-status",
      render: (row: any) => {
        const statusValue = fetchValueByKeyOfDropdown(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.othersStatusList,
          row.status,
        );
        return (
          <MuiBox className="badge-status">
            <StatusBadge status={row.status} label={statusValue} />
          </MuiBox>
        );
      },
    },
    {
      id: CreateClientDocumentsFormKey.documentOrigin,
      label: getLabel(documentSiteCoreKeys.documentOrigin),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {fetchValueByKeyOfDropdown(
              documentSiteCoreKeys.documentsTitle,
              documentSiteCoreKeys.documentOriginList,
              row?.documentOrigin,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: CreateClientDocumentsFormKey.notes,
      label: commonLabels.notes,
      isSortVisible: true,
      className: "cell-notes",
      render: (row: any) => (
        <MuiBox>
          <MuiNotesToolTip message={row.notes} />
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(documentSiteCoreKeys.action),
      className: "cell-action",
      render: (row: any) => (
        <MuiBox>
          {isEditable ? (
            <OptionMenu
              clientData={row}
              onClickOptionEdit={() =>
                handleEditOtherDocumentClick(row.documentId, row.documentType)
              }
              onClickArchive={archiveOtherDocumentAction}
              onClickOptionDelete={deleteOtherDocumentAction}
              onClickOptionNoLongerValid={noLongerValidOtherDocumentAction}
              onClickOptionMarkAsActive={markAsActiveOtherDocumentAction}
              onClickOptionUnMap={unMapInheritedOtherDocumentAction}
            />
          ) : (
            <></>
          )}
        </MuiBox>
      ),
    },
  ];

  const tableHeaderForm = [
    <FormController
      key="documentName"
      control={control}
      name={CreateClientDocumentsFormKey.documentName}
    >
      <MuiTextField
        id={`${pageId}-${CreateClientDocumentsFormKey.documentName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        showCloseIcon
      />
    </FormController>,
    <FormController
      key={"usedByClient"}
      control={control}
      name={CreateClientDocumentsFormKey.usedByClient}
    >
      <MuiTextField
        key={"usedByClient"}
        id={`${pageId}-${CreateClientDocumentsFormKey.usedByClient}`}
        aria-label={commonLabels.search}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        showCloseIcon={true}
        placeholder={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        requiredARIALabel={commonARIALabel.required}
        shouldTrimSpaces={false}
      />
    </FormController>,
    <FormController
      key="status"
      control={control}
      name={CreateClientDocumentsFormKey.status}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.othersStatusList,
          true,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.notes}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <FormController
      key="documentOrigin"
      control={control}
      name={CreateClientDocumentsFormKey.documentOrigin}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={fetchDropdownValues(
          documentSiteCoreKeys.documentsTitle,
          documentSiteCoreKeys.documentOriginList,
          true,
        )}
        value=""
        id={`${pageId}-${CreateClientDocumentsFormKey.documentOrigin}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
  ];

  const confirmationMessage = useCallback(() => {
    if (selectedDocumentActionRef.current === ACTION_ACTIVE) {
      return getLabel(documentSiteCoreKeys.documentActiveConfirmationboxText);
    } else if (selectedDocumentActionRef.current === ACTION_NO_LONGER_VALID) {
      return getLabel(documentSiteCoreKeys.confirmPopupForMarkAsNoLongerValid);
    } else if (selectedDocumentActionRef.current === ACTION_ARCHIVE) {
      return getLabel(documentSiteCoreKeys.documentArchiveConfirmation);
    }
  }, []);

  const handleEditOtherDocumentClick = (
    documentId: string,
    documentType: string,
  ) => {
    onDocumentEdit?.(
      documentId,
      documentType === "contract"
        ? DOCUMENT_SITE_CORE_KEY.documentTypeContract
        : DOCUMENT_SITE_CORE_KEY.documentTypeOthers,
    );
  };

  const handleCloseArchiveModal = () => {
    setShowArchiveContractModal(false);
  };

  const handleArchiveContractDocumentConfirmation = (
    archivalReason: string,
    archivalComments = "",
  ) => {
    const documentId =
      selectedArchiveDocumentIndexRef.current?.documentId ?? "";
    const params: ArchiveStatusOtherDocumentParams = {
      documentId,
      archivalReason,
      archivalComments,
    };
    if (isNotEmpty(documentId)) {
      setShowArchiveContractModal(false);
      archiveStatusContractDocument(params);
    }
  };

  return (
    <OtherDocumentListWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        isLastColumnSticky={isEditable}
        tableResults={[...filteredResults]}
        noDataInApi={rows.length === 0}
        noResultMessage={commonLabels.noResult}
        emptyMessage={getLabel(documentSiteCoreKeys.noRecordsAvailable)}
        priorityResults={[]}
        isFooterVisible={false}
      />
      {showDeleteOtherDocumentModal && (
        <OtherDocumentWarningConfirmationModal
          show={showDeleteOtherDocumentModal}
          onClose={handleDeleteOtherDocumentModalClose}
          onPrimaryButtonClick={handleOtherDocumentDeleteClick}
          labelContent={getLabel(documentSiteCoreKeys.deleteDocumentActionText)}
        />
      )}
      {showStatusUpdateOtherModal && (
        <OtherDocumentWarningConfirmationModal
          show={showStatusUpdateOtherModal}
          onClose={handleDeleteOtherDocumentModalClose}
          secondaryButtonTitle={commonLabels.no}
          modalTitle={commonLabels.confirmation}
          onPrimaryButtonClick={handleOtherDocumentAction}
          labelContent={confirmationMessage() ?? ""}
        />
      )}
      {showArchiveContractModal && (
        <ArchiveContractDocumentModal
          open={showArchiveContractModal}
          onClose={handleCloseArchiveModal}
          documentData={selectedArchiveDocumentIndexRef.current}
          onConfirm={handleArchiveContractDocumentConfirmation}
        />
      )}
    </OtherDocumentListWrapper>
  );
};
